import React, { useContext, useState } from 'react';

import { withURL, withCustomPaths, withProductTemplateThumbnails } from '@lucid/utils';
import { useSmartlingStrings } from '@lucid/hooks';
import kebabCase from 'lodash/kebabCase';

import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import LayoutContext from '../context/layout-context';
import Pagination from '../pagination/pagination';
import TemplateCard from '../cards/template-card/template-card';
import type { Lucid } from '../../types/types';

interface ChildCategorySectionProps {
  categories?: Lucid.Template.ProductTemplate.Data[];
  categoryType?: string;
  pageSize?: number;
  startPage?: number;
  pParam?: string;
  sParam?: string;
  setPageParam?: (value: string) => void;
  heading?: string;
}

const ChildCategorySection: React.FC<ChildCategorySectionProps> = ({
  categories = [],
  categoryType,
  pageSize = 12,
  setPageParam,
  pParam,
  sParam,
  startPage = 1,
  heading,
}) => {
  const { urlLangPrefix, shortCode, product } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_consideration_template',
      'mktg_product_template_hub_used_with',
      'mktg_product_template_hub_view_more_like_this',
      'mktg_search_no_results',
    ],
    shortCode,
  });

  const [page, setPage] = useState<number>(startPage);

  const handleNewPage = (value: number) => {
    setPage(value);

    if (setPageParam) {
      setPageParam(value.toString());
    }
  };

  return (
    <Container>
      <Box css={theme => ({ width: '100%', marginBottom: theme.space.s64 })}>
        <Box
          css={theme => ({
            backgroundColor: theme.colors.coolGrey[5],
            padding: theme.space.s40,
            paddingBottom: 0,
            borderRadius: theme.radii.r12,
            '& > h2': {
              textAlign: 'center',
            },
          })}
        >
          {heading && (
            <Heading as="h2" css={theme => ({ paddingBottom: theme.space.s40, margin: 0 })}>
              {heading}
            </Heading>
          )}
          {page && (
            <Pagination<Lucid.Template.ProductTemplate.Data>
              aria-label="template search results"
              items={categories}
              itemsPerPage={pageSize}
              onClick={sParam ? handleNewPage : undefined}
              pageLink={page =>
                withURL(
                  `${withCustomPaths(shortCode, product, 'product-template-category')}/${`${kebabCase(categoryType)}`}${page > 1 ? `/${page}` : ''}`,
                  {
                    customParams: sParam
                      ? [
                          {
                            s: sParam || '',
                          },
                          {
                            p: pParam || `${page}`,
                          },
                        ]
                      : undefined,
                    urlLangPrefix,
                    product,
                    willPrependLangPrefix: true,
                  },
                )
              }
              startPage={page}
              visiblePageCount={3}
            >
              {({ visibleItems }) =>
                visibleItems.length > 0 ? (
                  <Box
                    css={theme => ({
                      display: 'grid',
                      justifyItems: 'center',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: theme.space.s24,
                      width: '100%',
                      [theme.breakpoints.bp1024.max]: {
                        gridTemplateColumns: 'repeat(2, 1fr)',
                      },
                      [theme.breakpoints.bp768.max]: {
                        gridTemplateColumns: '1fr',
                      },
                    })}
                  >
                    {visibleItems.map(template => {
                      const image = {
                        imageAlt: withSmartling('mktg_consideration_template', {
                          template: template.Document.title,
                        }),
                        imageSrc: withProductTemplateThumbnails({
                          templateId: template.templateId,
                          params: {
                            thumbnailSize: 516,
                          },
                        }),
                      };

                      const {
                        Document: { title, product_id },
                        Template,
                      } = template;

                      return (
                        <TemplateCard
                          key={`paginated-${template.templateId}`}
                          heading={Template?.description || title}
                          integrationText={`${withSmartling('mktg_product_template_hub_used_with')}:`}
                          link={{
                            final_url: template.path.url,
                            accessibleDescription: withSmartling('mktg_consideration_template', {
                              template: template.Document.title,
                            }),
                          }}
                          media={{
                            image: {
                              filename_disk: image.imageSrc,
                              translations: [{ alt: image.imageAlt }],
                            } as Lucid.Directus.Generated.Directus_Files,
                          }}
                          productId={product_id}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Heading as="h2" variant="headingM">
                    {withSmartling('mktg_search_no_results')}
                  </Heading>
                )
              }
            </Pagination>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default ChildCategorySection;
